import { Box, Divider, Grid, IconButton, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { useEffect, useState, useCallback, useMemo } from "react";
import { ActionButton } from "../../../../components/Button";
import Dialog, { DialogSectionTitle } from "../../../../components/Dialog";
import { IAddEditDialogProps } from "../../../../components/Dialog";
import TextField from "../../../../components/TextField";
import Switch from "../../../../components/Switch";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import IEnterprise from "../../../../interfaces/IEnterprise";
import ValidationHelper from '../../../../helpers/ValidationHelper';
import DefaultConstants from "../../../../data/Constants";
import { GovernanceStatusData, RegistrationStatusEnum, RoomStatusData, RoomStatusEnum } from "../../../../data/Enums";
import FormHelper from "../../../../helpers/FormHelper";
import EnterpriseService from '../../../../services/EnterpriseService';
import ImageField from "../../../../components/ImageField";
import DateField from "../../../../components/DateField";
import SelectField from "../../../../components/SelectField";
import { useEnvironmentContext } from "../../../../contexts/EnvironmentContext";
import { Fastfood, NetworkCheck } from "@mui/icons-material";
import { useUserContext } from "../../../../contexts/UserContext";
import Tooltip from "../../../../components/Tooltip";


const AddEditDialog: React.FunctionComponent<IAddEditDialogProps<IEnterprise>> = ({
    open,
    handleClose,
    data,
    handleListRefresh = () => {}
}) => {
    const { translate } = useLanguageContext();
    const { environment } = useEnvironmentContext();
    const { user } = useUserContext();

    const [name, setName] = useState<string>('');
    const [expiresAt, setExpiresAt] = useState<Date | undefined>(undefined);
    const [partnerId, setPartnerId] = useState<string>('');
    const [actived, setActived] = useState<boolean>(true);
    const [databaseTypeId, setDatabaseTypeId] = useState<number | undefined>(undefined);
    const [databaseHost, setDatabaseHost] = useState<string>('');
    const [databasePort, setDatabasePort] = useState<string>('');
    const [databaseName, setDatabaseName] = useState<string>('');
    const [databaseUser, setDatabaseUser] = useState<string>('');
    const [databasePassword, setDatabasePassword] = useState<string>('');
    const [apiBaseUrl, setApiBaseUrl] = useState<string>('');
    const [apiAccessToken, setApiAccessToken] = useState<string>('');
    const [ownerGuestTypeId, setOwnerGuestTypeId] = useState<string>('');
    const [vipGuestTypeId, setVipGuestTypeId] = useState<string>('');
    const [roomServicePointOfSaleId, setRoomServicePointOfSaleId] = useState<string>('');
    const [requireMandatoryGovernanceItemsMarked, setRequireMandatoryGovernanceItemsMarked] = useState<boolean>(false);
    const [governanceStatusAllowedToDisplayGovernanceItems, setGovernanceStatusAllowedToDisplayGovernanceItems] = useState<string[]>([]);
    const [showOnlyParameterizedRoomTypes, setShowOnlyParameterizedRoomTypes] = useState<boolean>(false);
    const [showOnlyRoomsFromPool, setShowOnlyRoomsFromPool] = useState<boolean>(false);
    const [analyticsUrl, setAnalyticsUrl] = useState<string>('');


    useEffect(() => {
        if (open) {
            if (data) {
                setName(data.name);
                setExpiresAt(data.expiresAt);
                setPartnerId(data.partnerId);
                setActived(Number(data.registrationStatusId) === RegistrationStatusEnum.Actived);
                setDatabaseTypeId(data.databaseTypeId);
                setDatabaseHost(data.databaseHost);
                setDatabasePort(data.databasePort);
                setDatabaseName(data.databaseName);
                setDatabaseUser(data.databaseUser);
                setDatabasePassword(data.databasePassword);
                setApiBaseUrl(data.apiBaseUrl);
                setApiAccessToken(data.apiAccessToken);
                setOwnerGuestTypeId(data.ownerGuestTypeId);
                setVipGuestTypeId(data.vipGuestTypeId);
                setRoomServicePointOfSaleId(data.roomServicePointOfSaleId);
                setRequireMandatoryGovernanceItemsMarked(data.requireMandatoryGovernanceItemsMarked);
                setGovernanceStatusAllowedToDisplayGovernanceItems(data.governanceStatusAllowedToDisplayGovernanceItems);
                setShowOnlyParameterizedRoomTypes(data.showOnlyParameterizedRoomTypes);
                setShowOnlyRoomsFromPool(data.showOnlyRoomsFromPool);
                setAnalyticsUrl(data.analyticsUrl);
            } else {
                setName('');
                setExpiresAt(undefined);
                setPartnerId('');
                setActived(true);
                setDatabaseTypeId(undefined);
                setDatabaseHost('');
                setDatabasePort('');
                setDatabaseName('');
                setDatabaseUser('');
                setDatabasePassword('');
                setApiBaseUrl('');
                setApiAccessToken('');
                setOwnerGuestTypeId('');
                setVipGuestTypeId('');
                setRoomServicePointOfSaleId('');
                setRequireMandatoryGovernanceItemsMarked(false);
                setGovernanceStatusAllowedToDisplayGovernanceItems([]);
                setShowOnlyParameterizedRoomTypes(false);
                setShowOnlyRoomsFromPool(false);
                setAnalyticsUrl('');
            }
        }

        // eslint-disable-next-line
    }, [open]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        let formData = FormHelper.Create(event);
        if (data?.id) {
            formData = FormHelper.Add(formData, 'id', data.id);
        }

        EnterpriseService.AddEdit(formData).then(() => {
            handleClose();
            handleListRefresh();
        });
        
        // eslint-disable-next-line
    }, [data]);


    const handleTestDatabaseConnection = useCallback(() => {
        let formData = FormHelper.Create(undefined);
        formData = FormHelper.Add(formData, 'databaseTypeId', databaseTypeId);
        formData = FormHelper.Add(formData, 'databaseHost', databaseHost);
        formData = FormHelper.Add(formData, 'databasePort', databasePort);
        formData = FormHelper.Add(formData, 'databaseName', databaseName);
        formData = FormHelper.Add(formData, 'databaseUser', databaseUser);
        formData = FormHelper.Add(formData, 'databasePassword', databasePassword);

        EnterpriseService.TestDatabaseConnection(formData);
        // eslint-disable-next-line
    }, [databaseTypeId, databaseHost, databasePort, databaseName, databaseUser, databasePassword]);


    const handleTestApiConnection = useCallback(() => {
        let formData = FormHelper.Create(undefined);
        formData = FormHelper.Add(formData, 'apiBaseUrl', apiBaseUrl);
        formData = FormHelper.Add(formData, 'apiAccessToken', apiAccessToken);

        EnterpriseService.TestApiConnection(formData);
        // eslint-disable-next-line
    }, [apiBaseUrl, apiAccessToken]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='cancel' onClick={handleClose} />,
            <ActionButton action='save' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);


    return (
        <Dialog
            open={open}
            handleClose={handleClose}
            title={translate(data ? 'edit' : 'add')}
            maxWidth='md'
            fullWidth={true}
            fullHeight={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween'
        >
            <Grid container columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item md={8} xs={12}>
                    <TextField 
                        label={translate('name')} 
                        name='name' 
                        value={name} 
                        onChange={e => ValidationHelper.MaxLength(e.target.value, 100, setName)}
                    />
                </Grid>

                <Grid item md={4} xs={12}>
                    <DateField
                        label={translate('expiresAt')}
                        format='DD/MM/YYYY'
                        name='expiresAt'
                        value={expiresAt}
                        onChange={value => setExpiresAt(value)}
                        disabled={data && !user?.administrator}
                    />
                </Grid>

                <Grid item md={8} xs={12}>
                    <TextField 
                        label={translate('token')}
                        value={data?.token ?? ''} 
                        disabled={true}
                        required={false}
                    />
                </Grid>

                <Grid item md={4} xs={12}>
                    <TextField 
                        label={translate('partnerId')} 
                        name='partnerId' 
                        value={partnerId} 
                        required={!data?.administrator}
                        onChange={e => ValidationHelper.MaxLength(e.target.value, 20, setPartnerId)}
                    />
                </Grid>

                <Grid item xs={12} className='textalign-right'>
                    <Switch
                        label={translate('actived')}
                        name='registrationStatusId'
                        value={actived ? RegistrationStatusEnum.Actived : RegistrationStatusEnum.Inactived}
                        checked={actived}
                        onChange={e => setActived(e.target.checked)}
                    />
                </Grid>

                <Grid item xs={12} marginY={4}>
                    <Divider />
                </Grid>

                <Grid item xs={12}>
                    <DialogSectionTitle title={translate('images')} />
                </Grid>

                <Grid item md={4} sm={6} xs={12} marginTop={2}>
                    <ImageField
                        title={translate('logo')}
                        name='logo'
                        defaultImage={data?.logo}
                        height={200}
                        required={false}
                    />
                </Grid>

                <Grid item md={4} sm={6} xs={12} marginTop={2}>
                    <ImageField
                        title={translate('illustration')}
                        name='illustration'
                        defaultImage={data?.illustration}
                        height={200}
                        required={false}
                    />
                </Grid>

                <Grid item xs={12} marginY={4}>
                    <Divider />
                </Grid>

                <Grid item md={6} xs={8}>
                    <DialogSectionTitle title={translate('database')} />
                </Grid>

                <Grid item md={6} xs={4} className='textalign-right'>
                    <Tooltip title={translate('testDatabaseConnection')}>
                        <IconButton onClick={handleTestDatabaseConnection}>
                            <NetworkCheck />
                        </IconButton>
                    </Tooltip>
                </Grid>

                <Grid item md={6} xs={12} marginTop={1}>
                    <SelectField 
                        label={translate('type')} 
                        name='databaseTypeId' 
                        value={databaseTypeId}
                        onChange={e => setDatabaseTypeId(e.target.value)}
                        required={!data?.administrator}
                        data={environment.databaseTypeList.map(d => ({ value: d.id, text: d.description }))}
                    />
                </Grid>

                <Grid item md={8} xs={12}>
                    <TextField 
                        label={translate('host')} 
                        name='databaseHost' 
                        value={databaseHost} 
                        required={!data?.administrator}
                        onChange={e => ValidationHelper.MaxLength(e.target.value, 200, setDatabaseHost)}
                    />
                </Grid>

                <Grid item md={4} xs={12}>
                    <TextField 
                        label={translate('port')} 
                        name='databasePort' 
                        value={databasePort} 
                        required={!data?.administrator}
                        type='number'
                        onChange={e => ValidationHelper.MaxLength(e.target.value, 10, setDatabasePort)}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <TextField 
                        label={translate('databaseName')} 
                        name='databaseName' 
                        value={databaseName} 
                        required={!data?.administrator}
                        onChange={e => ValidationHelper.MaxLength(e.target.value, 50, setDatabaseName)}
                    />
                </Grid>

                <Grid item md={3} xs={12}>
                    <TextField 
                        label={translate('user')} 
                        name='databaseUser' 
                        value={databaseUser} 
                        required={!data?.administrator}
                        onChange={e => ValidationHelper.MaxLength(e.target.value, 50, setDatabaseUser)}
                    />
                </Grid>

                <Grid item md={3} xs={12}>
                    <TextField 
                        label={translate('password')} 
                        name='databasePassword' 
                        value={databasePassword} 
                        required={!data?.administrator}
                        type='password'
                        onChange={e => ValidationHelper.MaxLength(e.target.value, 50, setDatabasePassword)}
                    />
                </Grid>

                <Grid item xs={12} marginY={4}>
                    <Divider />
                </Grid>

                <Grid item md={6} xs={8}>
                    <DialogSectionTitle title={translate('api')} />
                </Grid>

                <Grid item md={6} xs={4} className='textalign-right'>
                    <Tooltip title={translate('testApiConnection')}>
                        <IconButton onClick={handleTestApiConnection}>
                            <NetworkCheck />
                        </IconButton>
                    </Tooltip>
                </Grid>

                <Grid item md={6} xs={12}>
                    <TextField 
                        label={translate('baseUrl')} 
                        name='apiBaseUrl' 
                        value={apiBaseUrl} 
                        required={!data?.administrator}
                        onChange={e => ValidationHelper.MaxLength(e.target.value, 200, setApiBaseUrl)}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <TextField 
                        label={translate('accessToken')} 
                        name='apiAccessToken' 
                        value={apiAccessToken} 
                        required={!data?.administrator}
                        onChange={e => ValidationHelper.MaxLength(e.target.value, 50, setApiAccessToken)}
                    />
                </Grid>

                <Grid item xs={12} marginY={4}>
                    <Divider />
                </Grid>

                <Grid item xs={12}>
                    <DialogSectionTitle title={environment.partnerPms.governanceModuleName} />
                </Grid>

                <Grid item md={8} xs={12}>
                    <SelectField
                        label={translate('governanceStatusAllowedToDisplayGovernanceItems')}
                        name='governanceStatusAllowedToDisplayGovernanceItems'
                        value={governanceStatusAllowedToDisplayGovernanceItems}
                        required={false}
                        multiple={true}
                        onChange={e => setGovernanceStatusAllowedToDisplayGovernanceItems(e.target.value)}
                        data={Object.values(GovernanceStatusData).filter(s => s.partnerId).map(s => {
                            return {
                                value: s.partnerId,
                                text: translate(s.name)
                            };
                        })}
                    />
                </Grid>

                <Grid item xs={12} marginLeft={1}>
                    <Switch
                        label={translate('requireMandatoryGovernanceItemsMarked')}
                        name='requireMandatoryGovernanceItemsMarked'
                        value={requireMandatoryGovernanceItemsMarked}
                        checked={requireMandatoryGovernanceItemsMarked}
                        onChange={e => setRequireMandatoryGovernanceItemsMarked(e.target.checked)}
                    />
                </Grid>

                <Grid item xs={12} marginTop={2} />

                <Grid item md={6} xs={12} marginTop={2}>
                    <Typography variant='subtitle1' component='span' className='fontsize-14 fontweight-600' paddingLeft={0.4}>
                        {translate('guestTypes')}
                    </Typography>

                    <Box marginBottom={1} />

                    <Table className='borderradius-default backgroundcolor-grey-1'>
                        <TableBody>
                            {
                                [
                                    {
                                        label: translate('owner'),
                                        name: 'ownerGuestTypeId',
                                        value: ownerGuestTypeId,
                                        enumProps: RoomStatusData[RoomStatusEnum.OwnerReservation],
                                        onChange: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => ValidationHelper.MaxLength(e.target.value, 20, setOwnerGuestTypeId)
                                    }, {
                                        label: translate('vip'),
                                        name: 'vipGuestTypeId',
                                        value: vipGuestTypeId,
                                        enumProps: RoomStatusData[RoomStatusEnum.VipReservation],
                                        onChange: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => ValidationHelper.MaxLength(e.target.value, 20, setVipGuestTypeId)
                                    }
                                ].map((row, index) => {
                                    return <TableRow key={index}>
                                        <TableCell>
                                            <Box className='display-flex alignitems-center'>
                                                <row.enumProps.Icon sx={{ color: `${row.enumProps.color} !important` }} />
                                                <Typography variant="subtitle2" className='textwrap-nowrap' marginLeft={1.2}>{row.label}</Typography>
                                            </Box>
                                        </TableCell>

                                        <TableCell className='padding-none'>
                                            <TextField 
                                                name={row.name} 
                                                value={row.value}
                                                required={false}
                                                onChange={e => row.onChange(e)}
                                                fullWidth={false}
                                                margin="none"
                                                placeholder="..."
                                                InputProps={{
                                                    disableUnderline: true,
                                                    className: 'backgroundcolor-transparent borderradius-none'
                                                }}
                                                inputProps={{
                                                    className: 'textalign-right',
                                                    style: {
                                                        paddingTop: 16,
                                                        paddingRight: 12,
                                                        paddingBottom: 16,
                                                        paddingLeft: 12,
                                                    },
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                })
                            }
                        </TableBody>
                    </Table>
                </Grid>

                <Grid item md={6} xs={12} marginTop={2}>
                    <Typography variant='subtitle1' component='span' className='fontsize-14 fontweight-600' paddingLeft={0.4}>
                        {translate('pointsOfSale')}
                    </Typography>

                    <Box marginBottom={1} />

                    <Table className='borderradius-default backgroundcolor-grey-1'>
                        <TableBody>
                            {
                                [
                                    {
                                        label: translate('roomService'),
                                        name: 'roomServicePointOfSaleId',
                                        value: roomServicePointOfSaleId,
                                        onChange: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => ValidationHelper.MaxLength(e.target.value, 20, setRoomServicePointOfSaleId)
                                    }
                                ].map((row, index) => {
                                    return <TableRow key={index}>
                                        <TableCell>
                                            <Box className='display-flex alignitems-center'>
                                                <Fastfood />
                                                <Typography variant="subtitle2" className='textwrap-nowrap' marginLeft={1.2}>{row.label}</Typography>
                                            </Box>
                                        </TableCell>

                                        <TableCell className='padding-none'>
                                            <TextField 
                                                name={row.name} 
                                                value={row.value}
                                                required={false}
                                                onChange={e => row.onChange(e)}
                                                fullWidth={false}
                                                margin="none"
                                                placeholder="..."
                                                InputProps={{
                                                    disableUnderline: true,
                                                    className: 'backgroundcolor-transparent borderradius-none'
                                                }}
                                                inputProps={{
                                                    className: 'textalign-right',
                                                    style: {
                                                        paddingTop: 16,
                                                        paddingRight: 12,
                                                        paddingBottom: 16,
                                                        paddingLeft: 12,
                                                    },
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                })
                            }
                        </TableBody>
                    </Table>
                </Grid>

                <Grid item xs={12} marginTop={4} />

                <Grid item xs={12} marginLeft={1}>
                    <Switch
                        label={translate('showOnlyRoomsFromPool')}
                        name='showOnlyRoomsFromPool'
                        value={showOnlyRoomsFromPool}
                        checked={showOnlyRoomsFromPool}
                        onChange={e => setShowOnlyRoomsFromPool(e.target.checked)}
                    />
                </Grid>

                <Grid item xs={12} marginLeft={1}>
                    <Switch
                        label={translate('showOnlyParameterizedRoomTypes')}
                        name='showOnlyParameterizedRoomTypes'
                        value={showOnlyParameterizedRoomTypes}
                        checked={showOnlyParameterizedRoomTypes}
                        onChange={e => setShowOnlyParameterizedRoomTypes(e.target.checked)}
                    />
                </Grid>

                <Grid item xs={12} marginY={4}>
                    <Divider />
                </Grid>

                <Grid item xs={12}>
                    <DialogSectionTitle title={environment.partnerPms.analyticsModuleName} />
                </Grid>

                <Grid item xs={12}>
                    <TextField 
                        type='url'
                        label={translate('powerBiUrl')} 
                        name='analyticsUrl' 
                        value={analyticsUrl} 
                        required={false}
                        onChange={e => ValidationHelper.MaxLength(e.target.value, 200, setAnalyticsUrl)}
                    />
                </Grid>
            </Grid>
        </Dialog>
    );
}

export default AddEditDialog;